import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { SimpleDateInputProps } from './types';
import { InputPattern, UnitText, SimpleDateInputContainer } from './styles';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import Input from '../Input/Input';

const PATTERNS = {
  simpleDate: '__/____',
  fullDate: '__/__/____',
};

const REGEXS = {
  simpleDate: /^\d{0,2}?(\/)?\d{0,4}$/,
  fullDate: /^\d{0,2}?(\/)?\d{0,2}?(\/)?\d{0,4}$/,
};

const LONG_MONTHS = [1, 3, 5, 7, 8, 10, 12];

const SimpleDateInput: React.FC<SimpleDateInputProps> = (props) => {
  const {
    input,
    meta,
    placeholder,
    label,
    readOnly,
    disabled,
    testId,
    size = 'standard',
    variant = 'underline',
    helperText,
    showClearIcon = true,
    unit,
    dateType = 'simpleDate',
    onChange,
    onBlur,
    onFocus,
    onMouseDown,
  } = props;
  const { actualTheme } = useContext(ThemeContext);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastValue = useRef('');
  const [internalValue, setInternalValue] = useState('');
  const [onChangeFlag, setonChangeFlag] = useState(false);
  const hasError = useMemo(() => meta && meta.invalid && meta.touched, [meta]);
  const inputValue = useMemo(() => input?.value || internalValue, [input?.value, internalValue]);
  const pattern = useMemo(() => PATTERNS[dateType].slice(inputValue?.length || 0, 11), [
    dateType,
    inputValue?.length,
  ]);

  const completeDoubleDigit = (value: number) => value.toString().padStart(2, '0');

  const currentDay = useMemo(() => completeDoubleDigit(new Date().getDate()), []);
  const currentMonth = useMemo(() => completeDoubleDigit(new Date().getMonth() + 1), []);
  const currentYear = useMemo(() => new Date().getFullYear().toString(), []);

  const isValidDay = (d: number) => Number.isInteger(d) && d >= 1 && d < 31;
  const isValidMonth = useCallback(
    (m: number) =>
      (Number.isInteger(m) && m >= 1 && m < 13) || (dateType === 'fullDate' && Number.isNaN(m)),
    [dateType],
  );
  const isValidYear = (y: number) => (Number.isInteger(y) && y >= 999) || Number.isNaN(y);

  const isValidDate = useCallback(
    (val: string) => {
      if (dateType === 'simpleDate') {
        const [monthStr, yearStr] = val.split('/');
        const month = Number(monthStr);
        const year = Number(yearStr);

        return isValidMonth(month) && isValidYear(year);
      }
      const [dayStr, monthStr, yearStr] = val.split('/');
      const day = Number(dayStr);
      const month = Number(monthStr);
      const year = Number(yearStr);
      return isValidDay(day) && isValidMonth(month) && isValidYear(year);
    },
    [dateType, isValidMonth],
  );

  const handleDateSeparator = useCallback(
    (val: string, len: number, lastLen: number) => {
      if (len === 2 && lastLen !== 3 && val.indexOf('/') === -1) {
        return `${val}/`;
      }
      if (len === 2 && lastLen === 3) {
        return val.slice(0, 1);
      }
      if (dateType === 'fullDate') {
        if (len === 5 && lastLen !== 6) {
          return `${val}/`;
        }
        if (len === 5 && lastLen === 6) {
          return val.slice(0, 4);
        }
      }
      return val;
    },
    [dateType],
  );

  const handleMaxFirstNumber = (val: string, len: number) => {
    if (val.indexOf('/') === -1) return `0${val[0]}/${val.slice(1)}`;
    if (len === 5) return `0${val.slice(1)}`;
    return `0${val}`;
  };

  const handleDateValidation = useCallback(
    (val: string) => {
      const regexp = REGEXS[dateType];
      if (!regexp.test(val)) {
        return null;
      }

      let validatedValue = val;
      const len = val.length;
      const lastLen = lastValue.current.length;
      if (isValidDate(val)) {
        validatedValue = handleDateSeparator(val, len, lastLen);
      } else if (dateType === 'simpleDate') {
        if (len > 0 && Number(val[0]) >= 2) {
          validatedValue = handleMaxFirstNumber(val, len);
        } else if (len > 2 && val.indexOf('/') === -1) {
          validatedValue = `${val.slice(0, 2)}/${val.slice(2)}`;
        }
      } else if (dateType === 'fullDate') {
        if (len > 0 && Number(val[0]) >= 4) {
          validatedValue = handleMaxFirstNumber(val, len);
        } else if (len > 3 && Number(val[3]) >= 2 && val.indexOf('/') === 2) {
          validatedValue = `${val.slice(0, 2)}/${handleMaxFirstNumber(val.slice(3), len)}`;
        } else if (len > 2 && val.indexOf('/') === -1) {
          validatedValue = `${val.slice(0, 2)}/${val.slice(2)}`;
        } else if (len > 5 && val.split('/').length - 1 === 1) {
          if (val.indexOf('/') === 2) validatedValue = `${val.slice(0, 5)}/${val.slice(5)}`;
          else validatedValue = `${val.slice(0, 2)}/${val.slice(2)}`;
        }
      }

      lastValue.current = validatedValue;

      return validatedValue;
    },
    [dateType, handleDateSeparator, isValidDate],
  );

  const handleChange = useCallback(
    (val: string) => {
      setonChangeFlag(true);
      const checkedValue = handleDateValidation(val);
      if (val === '' || checkedValue) {
        setInternalValue(checkedValue);
        if (input) input.onChange(checkedValue);
        if (onChange) onChange(checkedValue);
      }
    },
    [input, handleDateValidation, onChange],
  );

  const handleClear = useCallback(() => {
    if (input) {
      input.onChange('');
    }
    if (onChange) {
      onChange('');
    }
  }, [input, onChange]);

  const handleSimpleDateBlur = useCallback(() => {
    if (!input || !onChangeFlag) return;
    let localValue = internalValue;

    const [monthStr, yearStr] = internalValue.split('/');
    const month = Number(monthStr);
    const year = Number(yearStr);
    let monthResult = Number.isNaN(month) || month === 0 ? Number(currentMonth) : month;
    const yearResult = Number.isNaN(year) || !isValidYear(year) ? Number(currentYear) : year;
    monthResult = !isValidMonth(monthResult) ? 12 : monthResult;

    localValue = `${completeDoubleDigit(monthResult)}/${yearResult}`;

    if (localValue) input.onChange(localValue);
    input.onBlur();
    if (onBlur) onBlur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalValue, input, currentMonth, currentYear, onBlur]);

  const isLeapYear = (year: number) =>
    // eslint-disable-next-line no-nested-ternary
    year % 400 === 0 ? true : year % 100 === 0 ? false : year % 4 === 0;

  const getValidDay = (day: number, month: number, year: number) => {
    if (day <= 28) {
      return day;
    }
    let maxDay = 31;
    if (!LONG_MONTHS.includes(month)) {
      if (month !== 2) {
        maxDay = 30;
      } else {
        maxDay = isLeapYear(year) ? 29 : 28;
      }
    }
    return day <= maxDay ? day : maxDay;
  };

  const handleFullDateBlur = useCallback(() => {
    if (!input || !onChangeFlag) return;
    let localValue = internalValue;

    const [dayStr, monthStr, yearStr] = internalValue.split('/');

    const day = Number(dayStr);
    const month = Number(monthStr);
    const year = Number(yearStr);

    let dayResult = Number.isNaN(day) || day === 0 ? Number(currentDay) : day;
    let monthResult = Number.isNaN(month) || month === 0 ? Number(currentMonth) : month;
    const yearResult = Number.isNaN(year) || !isValidYear(year) ? Number(currentYear) : year;

    monthResult = !isValidMonth(monthResult) ? 12 : monthResult;
    dayResult = getValidDay(dayResult, monthResult, yearResult);

    localValue = `${completeDoubleDigit(dayResult)}/${completeDoubleDigit(
      monthResult,
    )}/${yearResult}`;

    if (localValue) input.onChange(localValue);
    input.onBlur();
    if (onBlur) onBlur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalValue, input, currentDay, currentMonth, currentYear, onBlur]);

  const handlePatternClick = useCallback(() => {
    if (inputRef?.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    setInternalValue(input.value);
  }, [input.value]);

  return (
    <ThemeProvider theme={actualTheme}>
      <SimpleDateInputContainer>
        <Input
          testId={testId}
          label={label}
          readOnly={readOnly || disabled}
          disabled={disabled}
          value={inputValue}
          input={input}
          meta={meta}
          autoComplete="off"
          size={size}
          variant={variant}
          helperText={helperText}
          showClearIcon={showClearIcon}
          placeholder={placeholder || ' '}
          onChange={handleChange}
          onClear={handleClear}
          rightAddOn={
            unit && (
              <UnitText size={size} hasError={hasError} data-testid={`${testId}-unit`}>
                {unit}
              </UnitText>
            )
          }
          onBlur={dateType === 'simpleDate' ? handleSimpleDateBlur : handleFullDateBlur}
          onFocus={onFocus}
          onMouseDown={onMouseDown}
        />
        {(inputValue?.length > 0 || !placeholder) && (
          <InputPattern
            variant={variant}
            hasError={hasError}
            size={size}
            isFullDate={dateType === 'fullDate'}
            onClick={handlePatternClick}
          >
            {pattern}
          </InputPattern>
        )}
      </SimpleDateInputContainer>
    </ThemeProvider>
  );
};

export default SimpleDateInput;
